import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { CreditCard, Download, Wallet, Info } from 'lucide-react';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(0);
  const [user, setUser] = useState(null);
  const [taxInfo, setTaxInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    try {
      const [pRes, uRes, tRes] = await Promise.all([
        api.get('/payments'),
        api.get('/users/profile'),
        api.get('/payments/preview').catch(() => ({ data: {} }))
      ]);
      setPayments(pRes.data.payments || []);
      const u = uRes.data.user;
      setUser(u);
      setBalance(parseFloat(u?.available_balance || 0));
      setTaxInfo(tRes.data);
    } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const requestPayout = async () => {
    if (!window.confirm('Vuoi richiedere il pagamento del saldo disponibile?')) return;
    setRequesting(true);
    try { const { data } = await api.post('/payments/request'); toast.success(data.message); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setRequesting(false); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/receipt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `ricevuta_${paymentId}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Ricevuta non disponibile'); }
  };

  const statusColors = { requested: 'bg-yellow-100 text-yellow-700', approved: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', on_hold: 'bg-orange-100 text-orange-700', rejected: 'bg-red-100 text-red-700' };
  const statusLabels = { requested: 'Richiesto', approved: 'Approvato', paid: 'Pagato', on_hold: 'In Attesa', rejected: 'Rifiutato' };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div>;

  const hasVat = !!user?.vat_number;

  return (
    <div>
      <h1 className="text-2xl font-bold text-pn-dark mb-6">Pagamenti</h1>

      {/* Balance + Request */}
      <div className="card p-6 mb-4 flex flex-col sm:flex-row items-center gap-6">
        <div className="flex-1 text-center sm:text-left">
          <p className="text-sm text-gray-500">Saldo Disponibile</p>
          <p className="text-4xl font-bold text-pn-dark">€{balance.toFixed(2)}</p>
        </div>
        <button onClick={requestPayout} disabled={requesting || balance <= 0} className="btn-primary px-8 py-3 text-base inline-flex items-center gap-2">
          <CreditCard size={20} />{requesting ? '...' : 'Richiedi Pagamento'}
        </button>
      </div>

      {/* Tax info preview */}
      {balance > 0 && taxInfo && (
        <div className="card p-4 mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-2">
            <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              {hasVat ? (
                taxInfo.vatExempt ? (
                  <p className="text-blue-800">Hai P.IVA registrata. IVA esente{taxInfo.vatExemptRef ? ` (${taxInfo.vatExemptRef})` : ''}. Riceverai <strong>€{balance.toFixed(2)}</strong>.</p>
                ) : (
                  <p className="text-blue-800">Hai P.IVA registrata. Il pagamento di <strong>€{balance.toFixed(2)}</strong> e IVA inclusa (Imponibile: €{(balance / (1 + (taxInfo.vatRate || 22) / 100)).toFixed(2)} + IVA {taxInfo.vatRate}%: €{(balance - balance / (1 + (taxInfo.vatRate || 22) / 100)).toFixed(2)}).</p>
                )
              ) : (
                taxInfo.withholdingExempt ? (
                  <p className="text-blue-800">Ritenuta d'acconto esente{taxInfo.withholdingExemptRef ? ` (${taxInfo.withholdingExemptRef})` : ''}. Riceverai <strong>€{balance.toFixed(2)}</strong>.</p>
                ) : (
                  <p className="text-blue-800">Sul pagamento verra applicata la ritenuta d'acconto ({taxInfo.withholdingRate}%). Riceverai <strong>€{(balance * (1 - (taxInfo.withholdingRate || 20) / 100)).toFixed(2)}</strong> (€{balance.toFixed(2)} - Ritenuta €{(balance * (taxInfo.withholdingRate || 20) / 100).toFixed(2)}).</p>
                )
              )}
              {!hasVat && <p className="text-xs text-blue-600 mt-1">Se hai P.IVA, aggiungila nel tuo Profilo per ricevere il pagamento con IVA.</p>}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <h2 className="font-bold text-pn-dark mb-4">Storico Pagamenti</h2>
      {payments.length === 0 ? (
        <div className="card p-8 text-center text-gray-400"><Wallet size={32} className="mx-auto mb-2 opacity-50" /><p>Nessun pagamento ancora</p></div>
      ) : (
        <div className="space-y-3">
          {payments.map(p => {
            const amount = parseFloat(p.amount_requested);
            const vat = parseFloat(p.vat_amount || 0);
            const withh = parseFloat(p.withholding_tax || 0);
            const net = parseFloat(p.net_payable);
            return (
              <div key={p.id} className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[p.status]}`}>{statusLabels[p.status]}</span></div>
                  <p className="text-sm text-gray-500 mt-1">Richiesto il: {new Date(p.requested_at).toLocaleDateString('it-IT')}</p>
                  {p.paid_at && <p className="text-sm text-green-600">Pagato il: {new Date(p.paid_at).toLocaleDateString('it-IT')}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="text-gray-400">Importo: €{amount.toFixed(2)}</p>
                  {p.has_vat && vat > 0 && <p className="text-gray-400">di cui IVA: €{vat.toFixed(2)}</p>}
                  {!p.has_vat && withh > 0 && <p className="text-gray-400">- Ritenuta: €{withh.toFixed(2)}</p>}
                  {(vat > 0 || withh > 0) && <p className="text-lg font-bold text-pn-dark">Netto: €{net.toFixed(2)}</p>}
                  {vat === 0 && withh === 0 && <p className="text-lg font-bold text-pn-dark">€{net.toFixed(2)}</p>}
                </div>
                {p.status === 'paid' && (
                  <button onClick={() => downloadReceipt(p.id)} className="text-xs px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 inline-flex items-center gap-1">
                    <Download size={14} />Ricevuta
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
